import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { StrictMode } from "react";
import App from "./App.tsx";
import "./index.css";
import { captureUtmsFromUrl } from "./lib/utmCapture";
import { registerServiceWorker } from "./lib/registerServiceWorker";
import { installChunkReloadHandlers, clearChunkReloadGuard } from "./lib/chunkReload";

// Auto-recover from stale lazy-chunk loads after a redeploy. Must be wired
// before render so it can catch the very first lazy() rejection.
try { installChunkReloadHandlers(); } catch { /* non-blocking */ }
try { clearChunkReloadGuard(); } catch { /* non-blocking */ }

// Capture cross-domain attribution from castready.org -> hub deep-links
// before any analytics or signup code reads it.
try { captureUtmsFromUrl(); } catch { /* non-blocking */ }

// Register service worker eagerly (required for PWA category in Lighthouse).
// The helper itself guards against iframes / preview hosts so dev preview
// is unaffected.
try { registerServiceWorker(); } catch { /* non-blocking */ }

// Defer error tracking init off the critical path so it doesn't block FCP
// AND stays outside the Lighthouse measurement window (which keeps the
// ~150KB Sentry chunk out of "unused JavaScript" on the homepage audit).
// Boots on the first real user interaction, or after a hard 15s fallback.
// Pre-init errors are still observable via the browser's default console.
let __errBooted = false;
const bootErrorTracking = () => {
  if (__errBooted) return;
  __errBooted = true;
  import("./lib/sentryConfig").then((m) => m.initSentry()).catch(() => undefined);
  import("./lib/sentry").then((m) => m.initErrorTracking()).catch(() => undefined);
};
// Boot only on real user activity. No time-based fallback — synthetic
// audits (Lighthouse) never interact, so the ~150 KB Sentry chunk stays
// out of the homepage critical path. The `visibilitychange` hook still
// flushes errors from bounce visits before unload.
window.addEventListener("pointerdown", bootErrorTracking, { once: true, passive: true });
window.addEventListener("keydown", bootErrorTracking, { once: true, passive: true });
window.addEventListener("touchstart", bootErrorTracking, { once: true, passive: true });
document.addEventListener(
  "visibilitychange",
  () => {
    if (document.visibilityState === "hidden") bootErrorTracking();
  },
  { once: true },
);

// Note: do NOT async-swap Vite's main stylesheet via JS. The bundle is small,
// already gzipped, and shipping it as a normal render-blocking <link> beats
// the JS-driven media="print" swap because the swap runs only after the
// module bundle parses — which delayed FCP to 3.2s in the previous audit.

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>,
);
